import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useCache, useController, useLoading } from '@data-client/react'

import { OverlayLoader } from 'src/components/loader'
import { fetchRefresh, fetchToken, removeToken, setToken } from 'src/services/tokens'

import { AccountResource } from '../api/account'
import { AccountEntity, type AccountFlag, TokenEntity } from '../datasource/account/account'
import { useNotification } from '../providers/Notification'
import routes from '../routes'
import { useConfig } from './ConfigProvider'
import { useNativeApp } from './NativeApp'

type ContextProps = {
  authenticate: (credentials: API.LoginRequest<'customer_password'>) => Promise<AccountEntity | null>
  ssoLogin: (token: string) => Promise<AccountEntity | null>
  authenticated: boolean | undefined
  logout: () => void
  user?: AccountEntity | null
  flag: AccountFlag
}

const defaultValues: ContextProps = {
  authenticate: async () => null,
  ssoLogin: async () => null,
  authenticated: undefined,
  logout: async () => undefined,
  flag: '',
}

const Auth = createContext<ContextProps>(defaultValues)

function AuthProvider({ children }: PropsWithChildren) {
  const { invalidateAll, fetch } = useController()
  const { company } = useConfig()
  const { notifyOnError } = useNotification()
  const navigate = useNavigate()
  const cached = useCache(AccountResource.me)
  const [user, setUser] = useState<AccountEntity | undefined>(cached)
  const { subscribe, unsubscribe } = useNativeApp()
  const [authenticated, setAuthenticated] = useState<boolean>()

  const fetchAccount = useCallback(async () => {
    return await fetch(AccountResource.me)
      .then(setUser)
      .then(() => {
        setAuthenticated(true)
        return true
      })
      .catch(() => {
        setAuthenticated(false)
        return false
      })
  }, [fetch])

  const init = useCallback(async () => {
    await fetchToken().then((token) => {
      if (token !== null) {
        fetchAccount().then((success) => {
          setAuthenticated(success)
          // When logged in via global network - there is a chance the user might get stuck on a company portal they do not have access to
          // This will log them out to prevent an infinite loop
          if (!success) clearUser()
        })
      } else {
        setAuthenticated(false)
      }
    })
  }, [fetchAccount])

  const clearUser = useCallback(() => {
    removeToken()
    setAuthenticated(false)
    setUser(undefined)
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOGOUT' }))
    invalidateAll({ testKey: (key) => !key.startsWith('/config') })
  }, [routes])

  const [logout, loggingOut] = useLoading(async () => {
    const refreshToken = fetchRefresh()
    if (!refreshToken) return
    return await fetch(AccountResource.logout, {
      refreshToken: refreshToken,
    })
      .then(() => clearUser())
      .then(() => navigate(routes.userLogin))
  }, [fetch, invalidateAll, removeToken, fetchRefresh, navigate])

  const handleAuth = useCallback(
    (token: TokenEntity) => {
      setToken(token)
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'customerNumber', data: token.customer.id }))
      setUser(token.customer)
      setAuthenticated(true)
      return token.customer
    },
    [setUser, setAuthenticated],
  )

  const authenticate = useCallback(
    async (credentials: API.LoginRequest<'customer_password'>) => {
      return await fetch(AccountResource.token, {
        ...credentials,
        companyId: company.id,
        grantType: 'customer_password',
      })
        .then(handleAuth)
        .catch((error) => {
          notifyOnError(error)
          setUser(undefined)
          setAuthenticated(false)
          return null
        })
    },
    [company.id, fetch, handleAuth, notifyOnError],
  )

  const ssoLogin = useCallback(
    async (token: string) => {
      return await fetch(AccountResource.sso, {
        accessToken: token,
        grantType: 'sso_token',
        companyId: company.id,
      })
        .then(handleAuth)
        .catch((error) => {
          clearUser()
          throw error
        })
    },
    [company.id, fetch, handleAuth, setUser],
  )

  useEffect(() => {
    init()

    const id = subscribe({
      event: 'TOKEN',
      callback: (token: TokenEntity) => {
        token && handleAuth(token)
      },
    })

    const logoutId = subscribe({
      event: 'LOGOUT',
      callback: () => {
        logout()
      },
    })

    return () => {
      unsubscribe(id)
      unsubscribe(logoutId)
    }
  }, [])

  if (loggingOut) {
    return <OverlayLoader fullscreen loading={loggingOut} />
  }

  return (
    <Auth.Provider
      value={{
        authenticate,
        ssoLogin,
        authenticated,
        user,
        logout,
        flag: user?.flag ?? '',
      }}
    >
      <OverlayLoader loading={loggingOut}>{children}</OverlayLoader>
    </Auth.Provider>
  )
}

const SessionProvider = AuthProvider

function useAuth(): ContextProps {
  return useContext(Auth)
}

export { Auth, AuthProvider, useAuth }

export default SessionProvider
